import { useRef } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { CHAT_EMOJI_OPTIONS } from '../../logic/chatUtils'
import { cx } from '../util/cx'
import styles from './ChatScreen.module.css'

interface ChatReactionMenuProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedEmoji?: string
  onEmoji: (emoji: string) => void
  onReply?: () => void
  layout?: 'bubble' | 'card'
  children: React.ReactNode
}

export default function ChatReactionMenu({
  open,
  onOpenChange,
  selectedEmoji,
  onEmoji,
  onReply,
  layout = 'bubble',
  children,
}: ChatReactionMenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null)

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor asChild>
        <div
          ref={anchorRef}
          className={cx(styles.bubbleAnchor, layout === 'card' && styles.bubbleAnchorCard)}
          onClick={() => onOpenChange(!open)}
        >
          {children}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className={styles.menu}
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.target
            if (target instanceof Node && anchorRef.current?.contains(target)) {
              event.preventDefault()
            }
          }}
        >
          <div className={styles.menuEmojis}>
            {CHAT_EMOJI_OPTIONS.map((emoji) => {
              const selected = emoji === selectedEmoji
              return (
                <button
                  key={emoji}
                  type="button"
                  className={cx(styles.menuBtn, selected && styles.menuBtnSelected)}
                  aria-label={selected ? `Remove ${emoji} reaction` : `React with ${emoji}`}
                  aria-pressed={selected}
                  onClick={(event) => {
                    event.stopPropagation()
                    onEmoji(emoji)
                  }}
                >
                  {emoji}
                </button>
              )
            })}
          </div>
          {onReply && (
            <button
              type="button"
              className={styles.menuReply}
              onClick={(event) => {
                event.stopPropagation()
                onReply()
              }}
            >
              Reply
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
